import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { queries } from '../api/queries.js';
import { Shell } from '../components/shell/Shell.js';
import { Loading } from '../components/ui/misc.js';
import { NotSignedIn } from '../features/auth/gate.js';
import { NoAccessScreen } from '../features/auth/NoAccessScreen.js';

/**
 * The pathless layout every page lives under — which is every page, now that this app has no
 * login screen of its own. Signing in happens at `auth.<domain>`; this decides only what to
 * render for someone who did not, or who did and lacks the role.
 *
 * The guard runs twice on purpose, as it did before: `beforeLoad` for navigations, and the
 * component for a session that dies while a page is open.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(queries.me());
    // Aborts the match, so no child loader fires a request that can only 401.
    if (!me.ok) throw new NotSignedIn(me.reason);
    return { me: me.me };
  },
  // A spinner for `no-session`, because the browser is already navigating to the central
  // login and naming a missing role would be wrong as well as briefly visible.
  errorComponent: ({ error }) =>
    error instanceof NotSignedIn && error.reason === 'no-role' ? <NoAccessScreen /> : <Loading full />,
  pendingComponent: () => <Loading full />,
  component: AppLayout,
});

function AppLayout(): ReactNode {
  const me = useQuery(queries.me());

  if (!me.data) return <Loading full />;
  // The role revoked, or the session ended, while this page was open: a 401 or 403 on any
  // request rewrites this cache entry (see api/client.ts) and lands here.
  if (!me.data.ok) return me.data.reason === 'no-role' ? <NoAccessScreen /> : <Loading full />;

  return (
    <Shell me={me.data.me}>
      <Outlet />
    </Shell>
  );
}
